const _excluded = ["id", "className", "label", "link", "onClick", "onMouseEnter", "onMouseLeave", "primary", "secondary", "destructive", "outline", "disabled", "fullWidth", "shouldRender", "style", "icon", "type", "data-test-element", "data-testid", "upperCase", "ariaHasPopUp", "omitDefaultStyles", "ariaExpanded"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
function _objectWithoutProperties(e, t) { if (null == e) return {}; var o, r, i = _objectWithoutPropertiesLoose(e, t); if (Object.getOwnPropertySymbols) { var s = Object.getOwnPropertySymbols(e); for (r = 0; r < s.length; r++) o = s[r], t.includes(o) || {}.propertyIsEnumerable.call(e, o) && (i[o] = e[o]); } return i; }
function _objectWithoutPropertiesLoose(r, e) { if (null == r) return {}; var t = {}; for (var n in r) if ({}.hasOwnProperty.call(r, n)) { if (e.includes(n)) continue; t[n] = r[n]; } return t; }
import React, { useRef, useImperativeHandle } from "react";
import { noop } from "lodash";
import cn from "../../../utils/cn";
import Icon from "../Icon/Icon";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const Button = /*#__PURE__*/React.forwardRef((props, ref) => {
  const {
      id = "",
      className = "",
      label = "",
      link = false,
      onClick = noop,
      onMouseEnter = noop,
      onMouseLeave = noop,
      primary = false,
      secondary = false,
      destructive = false,
      outline = false,
      disabled = false,
      fullWidth = false,
      shouldRender = true,
      style = {},
      icon = null,
      type = "button",
      "data-test-element": dataTestElement = null,
      "data-testid": dataTestId = null,
      upperCase = false,
      ariaHasPopUp,
      omitDefaultStyles = false,
      ariaExpanded
    } = props,
    rest = _objectWithoutProperties(props, _excluded);
  const buttonRef = useRef(null);
  const handleClick = e => {
    if (disabled) {
      e.preventDefault();
    } else {
      var _buttonRef$current;
      onClick(e);
      (_buttonRef$current = buttonRef.current) === null || _buttonRef$current === void 0 || _buttonRef$current.blur();
    }
  };
  useImperativeHandle(ref, () => buttonRef.current);
  const buttonClass = cn("inline-block font-regular-font py-2 px-4 rounded border border-solid focus:outline-solid-offset",
  // Base classes
  {
    // Conditional classes
    "text-sm font-heavy-font focus:border-focus-blue normal-case": !omitDefaultStyles,
    "bg-action-blue text-white border-transparent": primary && !omitDefaultStyles,
    "hover:bg-deep-blue": primary && !omitDefaultStyles && !disabled,
    "bg-white text-brand-blue border-light-border-blue": secondary && !omitDefaultStyles,
    "hover:bg-medium-fill-grey": secondary && !omitDefaultStyles && !disabled,
    "bg-transparent text-action-blue border-medium-border-blue": outline && !omitDefaultStyles,
    "hover:border-action-blue hover:text-deep-blue": outline && !omitDefaultStyles && !disabled,
    "bg-white border-button text-black": !primary && !secondary && !outline && !link && !omitDefaultStyles,
    "border-none bg-transparent text-action-blue": link,
    "bg-red text-white border-transparent": !primary && !secondary && !outline && destructive && !omitDefaultStyles,
    "hover:bg-dark-red": !primary && !secondary && !outline && destructive && !disabled && !omitDefaultStyles,
    "opacity-40 cursor-default": disabled,
    "cursor-pointer": !disabled,
    "w-full": fullWidth,
    uppercase: upperCase,
    "flex items-center justify-center": icon
  }, className // User-defined classes
  );
  const iconElement = icon !== null && icon !== void 0 && icon.name ? /*#__PURE__*/_jsx(Icon, {
    color: icon === null || icon === void 0 ? void 0 : icon.color,
    icon: icon.name,
    style: icon === null || icon === void 0 ? void 0 : icon.style,
    className: icon === null || icon === void 0 ? void 0 : icon.className,
    direction: icon === null || icon === void 0 ? void 0 : icon.direction
  }) : null;
  if (!shouldRender) {
    return null;
  }
  return /*#__PURE__*/_jsxs("button", _objectSpread(_objectSpread({
    id: id,
    ref: buttonRef,
    className: buttonClass,
    "aria-haspopup": ariaHasPopUp,
    "aria-label": label,
    onClick: handleClick,
    onMouseLeave: onMouseLeave,
    onMouseEnter: onMouseEnter,
    "aria-disabled": disabled,
    "aria-expanded": ariaExpanded,
    style: style,
    type: type,
    "data-test-element": dataTestElement,
    "data-testid": dataTestId
  }, rest), {}, {
    children: [iconElement && (icon === null || icon === void 0 ? void 0 : icon.position) === "left" && /*#__PURE__*/_jsx("span", {
      style: {
        color: 'inherit'
      },
      className: "inline-block mr-2 transform -translate-y-px",
      "aria-hidden": "true",
      children: iconElement
    }), label, iconElement && (icon === null || icon === void 0 ? void 0 : icon.position) === "right" && /*#__PURE__*/_jsx("span", {
      style: {
        color: 'inherit'
      },
      className: "inline-block ml-2 transform -translate-y-px",
      "aria-hidden": "true",
      children: iconElement
    })]
  }));
});
Button.displayName = 'Button';
export default Button;
//# sourceMappingURL=Button.js.map